/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { Box, Button, Grid } from '@mui/material';
import { useEffect } from 'react';
import { REPORT_TYPE, TAGS } from 'modules/dashboard/constants';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectBreakdownDetails,
  selectHotelDropdownList,
  selectMergedHotelList,
  selectVisualFilters,
} from 'modules/dashboard/selectors';
import { dashboardActions } from 'modules/dashboard/slice';
import { compareWindows } from 'modules/common/ui-controller';
import { hasLeapDay } from 'modules/common/utils/dateComparisonUtils';
import { getComparisonDateRange } from 'modules/dashboard/functions';
import ScenarioView from '../scenario-content';
import SearchFilter from '../filter-panel';
import { getAvailableFilters, getDateRange, moveHotelSetToEnd } from '../../functions';
import SearchVisual from './search-visual';
import { searchActions } from '../../slice';
import {
  getFocusOnFilter,
  getPercentageList,
  getSubBreakdownFilter,
  getSubPaceFilter,
} from '../../../inner-filter/functions';
import {
  selectSearchBookingDate,
  selectSearchCustomDate,
  selectSearchDateSelected,
  selectSelectedScenario,
} from '../../selectors';
import { SCENARIO_FILTERS } from '../../constants';

const VisualView = ({ clearScenario, latestDate }) => {
  const scenario = useSelector(selectSelectedScenario);
  const availableFilters = getAvailableFilters(scenario?.visuals);
  const visualDisplayNames = scenario?.visuals?.map((v) => v.displayName);
  const hotelDropdownList = useSelector(selectHotelDropdownList);
  const mergedHotelList = useSelector(selectMergedHotelList);
  const visualFilters = useSelector(selectVisualFilters);
  const bookingDate = useSelector(selectSearchBookingDate);
  const breakdownData = useSelector(selectBreakdownDetails);
  const selectedDate = useSelector(selectSearchDateSelected);
  const customDate = useSelector(selectSearchCustomDate);
  //
  const dispatch = useDispatch();
  //
  useEffect(() => {
    const filters = scenario?.visuals.flatMap((v) =>
      Object.entries(v.filters ?? {}).map(([key, filter]) => ({
        name: key,
        value: filter.value,
      }))
    );
    //
    moveHotelSetToEnd(filters).forEach((filter) => {
      switch (filter.name) {
        case SCENARIO_FILTERS.STAY_DATE: {
          let startDate;
          let endDate;

          if (typeof filter.value === 'string') {
            const dateRange = getDateRange(filter.value);
            //
            startDate = dateRange?.startDate;
            endDate = dateRange?.endDate;
          } else {
            startDate = filter.value.startDate;
            endDate = filter.value.endDate;
          }
          const stayDate = {
            key: 'selection',
            startDate,
            endDate,
          };
          const isLeapSelection = hasLeapDay(stayDate.startDate, stayDate.endDate);
          const dateRange = getComparisonDateRange(selectedDate, startDate, endDate, customDate);
          //
          dispatch(dashboardActions.setTargetDate(stayDate));
          dispatch(searchActions.setSearchStartDate(dateRange?.comparisonStartDate?.toISOString()));
          dispatch(searchActions.setSearchEndDate(dateRange?.comparisonEndDate?.toISOString()));
          dispatch(dashboardActions.setIsLeapSelection(isLeapSelection));
          break;
        }
        case SCENARIO_FILTERS.BOOKING_DATE: {
          let startDate;
          let endDate;

          if (typeof filter.value === 'string') {
            const dateRange = getDateRange(filter.value);
            //
            startDate = dateRange?.startDate;
            endDate = dateRange?.endDate;
          } else {
            startDate = filter.value.startDate;
            endDate = filter.value.endDate;
          }
          dispatch(searchActions.setSearchBookingDate({ ...bookingDate, startDate, endDate }));
          break;
        }
        case SCENARIO_FILTERS.HOTEL: {
          const foundHotel = [...hotelDropdownList, ...mergedHotelList].find(
            (hotel) => hotel?.label === filter?.value
          );
          if (foundHotel) {
            dispatch(dashboardActions.setHotelName(foundHotel));
          }

          break;
        }
        case SCENARIO_FILTERS.HOTEL_SET: {
          const foundHotel = [...hotelDropdownList, ...mergedHotelList].find(
            (hotel) => hotel?.label === filter?.value
          );
          if (foundHotel) {
            dispatch(searchActions.setHotelFocusOn(foundHotel));
          }
          break;
        }
        case SCENARIO_FILTERS.BREAKDOWN: {
          const found = getSubBreakdownFilter(visualFilters, TAGS.BREAKDOWN_SUMMARY).find(
            (breakdown) => breakdown.label === filter.value
          );
          if (found) {
            dispatch(searchActions.setBreakdown(found.id));
          }
          break;
        }
        case SCENARIO_FILTERS.BOOKING_PACE_HORIZON: {
          const foundPace = getSubPaceFilter(visualFilters).find((pace) =>
            pace.label?.includes(filter.value)
          );
          dispatch(dashboardActions.setPaceHorizon(foundPace?.id));
          break;
        }
        case SCENARIO_FILTERS.COMPARISON_PERIOD: {
          const dateComparison = filter.value;
          if (dateComparison === 'Previous Year') {
            dispatch(searchActions.setDateComparison(compareWindows.py));
          } else if (dateComparison === 'Previous Week') {
            dispatch(searchActions.setDateComparison(compareWindows.pw));
          } else if (dateComparison === 'Previous Month') {
            dispatch(searchActions.setDateComparison(compareWindows.pm));
          } else if (dateComparison === 'OTB') {
            dispatch(searchActions.setDateComparison(compareWindows.otb));
          } else {
            dispatch(searchActions.setDateComparison(compareWindows.cus));
          }
          break;
        }
        case SCENARIO_FILTERS.FOCUS_ON: {
          const { value } = filter;
          const focusOnList = getFocusOnFilter(breakdownData, value.breakdownName).filter((focus) =>
            value?.values?.includes(focus.label)
          );
          dispatch(searchActions.setFocusOn(focusOnList));
          break;
        }
        case SCENARIO_FILTERS.PERCENTAGE: {
          const found = getPercentageList(visualFilters).find(
            (percentage) => percentage.label === filter.value
          );
          dispatch(searchActions.setPercentage(found.id));
          break;
        }
        case SCENARIO_FILTERS.COMPARISON_ALIGNMENT: {
          dispatch(searchActions.setSearchAlignmentToggle(filter.value));
          break;
        }
        default:
          break;
      }
    });
  }, [scenario?.visuals]);

  return (
    <>
      <Grid item container justifyContent="flex-start" xs={12}>
        <Button onClick={clearScenario}>Back To Search</Button>
      </Grid>

      <Grid container>
        <Grid
          item
          xs={2}
          cc={2.5}
          md={2}
          sx={{
            ml: {
              xs: 0,
              cc: 0.25,
              md: 0,
            },
          }}
          direction="column"
        >
          <SearchFilter
            availableFilters={availableFilters}
            reportType={REPORT_TYPE.SEARCH}
            latestDate={latestDate}
          />
        </Grid>
        <Grid container item xs={10} cc={9.25} md={8.5} direction="row" spacing={8} ml={12}>
          <Grid item container xs={12}>
            <Box px={4} py={2} border={1} borderRadius={1} mx={8}>
              <ScenarioView scenario={scenario} />
            </Box>
          </Grid>
          <Grid item container xs={12}>
            {visualDisplayNames?.map((visualDisplayName) => (
              <SearchVisual visualDisplayName={visualDisplayName} />
            ))}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default VisualView;
